import React, { useState, useEffect, useCallback } from 'react'
import { Row, Col } from 'react-bootstrap'
import InputField from '@components/common/InputField/InputField.component'
import AccountsHeaderActionButton from './AccountsHeaderActionButton.component'
import PasswordOtpModal from './PasswordOtpModal.component'
import { useAccountsHeader } from '@pages/Accounts/AccountsHeader.context'
import { useProfile } from '@pages/Accounts/AccountsProfile.context'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  completePwChange,
  requestPwChangeOtp,
  verifyPwChangeOtp,
} from '@services/api/auth.api'
import {
  policyFail,
  PwRule,
  pwRuleLabel,
  pwRuleOrder,
  rulePass,
} from '@utils/passwordPolicy.util'
import { passwordMatch } from '@constants/loginReset.const'
import './PasswordSecurity.component.css'

/** Display-only mask (for security) */
const MASK = '••••••••'

const phase = {
  idle: 'idle',
  otp: 'otp',
  entry: 'entry',
} as const
type Phase = (typeof phase)[keyof typeof phase]

const PasswordSecurity: React.FC = () => {
  const { setHeaderButtons } = useAccountsHeader()
  const { profile, reload } = useProfile()
  const { showToast } = useNotificationToast()
  const [email, setEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [currentPhase, setCurrentPhase] = useState<Phase>(phase.idle)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const [otpId, setOtpId] = useState('')
  const [stepToken, setStepToken] = useState('')
  const [otpError, setOtpError] = useState('')
  const [otpSending, setOtpSending] = useState(false)
  const [otpVerifying, setOtpVerifying] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (profile?.email) setEmail(profile.email)
  }, [profile?.email])

  const resetAll = useCallback((): void => {
    setCurrentPhase(phase.idle)
    setNewPassword('')
    setConfirmNewPassword('')
    setShowNew(false)
    setShowConfirm(false)
    setOtpId('')
    setStepToken('')
    setOtpError('')
  }, [])

  const sendOtp = useCallback(
    async (isResend: boolean): Promise<boolean> => {
      setOtpSending(true)
      const res = await requestPwChangeOtp()
      setOtpSending(false)
      if (res.statusCode !== 200 || !res.data?.otp_id) {
        showToast?.(
          createFailedToast(
            'Could not send code',
            res.message || 'Please try again in a moment.',
          ),
        )
        return false
      }
      setOtpId(res.data.otp_id)
      setOtpError('')
      if (isResend) {
        showToast?.(
          createSuccessToast(
            'Code sent',
            'A new code has been emailed to you.',
          ),
        )
      }
      return true
    },
    [showToast],
  )

  const openOtp = useCallback(async (): Promise<void> => {
    const ok = await sendOtp(false)
    if (ok) setCurrentPhase(phase.otp)
  }, [sendOtp])

  const handleVerify = useCallback(
    async (code: string): Promise<void> => {
      if (!otpId) return
      setOtpVerifying(true)
      setOtpError('')
      const res = await verifyPwChangeOtp({ otpId, code })
      setOtpVerifying(false)
      if (res.statusCode !== 200 || !res.data?.step_token) {
        setOtpError(res.message || 'Invalid or expired code.')
        return
      }
      setStepToken(res.data.step_token)
      setCurrentPhase(phase.entry)
    },
    [otpId],
  )

  const handleResend = useCallback(async (): Promise<void> => {
    await sendOtp(true)
  }, [sendOtp])

  const handleCloseOtp = useCallback((): void => {
    resetAll()
  }, [resetAll])

  const handleSave = useCallback(async (): Promise<void> => {
    if (currentPhase !== phase.entry) return
    if (!stepToken) {
      showToast?.(
        createFailedToast(
          'Session expired',
          'Please restart the password update.',
        ),
      )
      resetAll()
      return
    }
    if (newPassword !== confirmNewPassword) {
      showToast?.(
        createFailedToast(
          'Invalid password',
          'New password must match confirmation.',
        ),
      )
      return
    }
    const ctx = {
      username: profile?.username ?? '',
      email: profile?.email ?? '',
    }
    const fail = policyFail(newPassword, ctx)
    if (fail) {
      showToast?.(createFailedToast('Invalid password', fail))
      return
    }
    setSaving(true)
    const res = await completePwChange({ stepToken, newPassword })
    setSaving(false)
    if (res.statusCode !== 200 || !res.data?.done) {
      showToast?.(
        createFailedToast('Error', res.message || 'Could not update password.'),
      )
      return
    }
    await reload()
    showToast?.(createSuccessToast('Saved', 'Password updated.'))
    resetAll()
  }, [
    confirmNewPassword,
    currentPhase,
    newPassword,
    profile?.email,
    profile?.username,
    reload,
    resetAll,
    showToast,
    stepToken,
  ])

  const handleCancel = useCallback((): void => {
    resetAll()
  }, [resetAll])

  useEffect(() => {
    const isEntry = currentPhase === phase.entry
    setHeaderButtons(
      <AccountsHeaderActionButton
        onClick={() => {
          if (saving) return
          if (currentPhase === phase.entry) {
            void handleSave()
          } else if (currentPhase === phase.otp) {
            resetAll()
          } else {
            void openOtp()
          }
        }}
        onCancel={isEntry ? handleCancel : undefined}
      >
        {saving
          ? 'Saving…'
          : otpSending && currentPhase === phase.idle
            ? 'Sending…'
            : isEntry
              ? 'Save changes'
              : 'Update password'}
      </AccountsHeaderActionButton>,
    )
    return () => setHeaderButtons(null)
  }, [
    currentPhase,
    handleCancel,
    handleSave,
    openOtp,
    otpSending,
    resetAll,
    saving,
    setHeaderButtons,
  ])

  return (
    <div className="accounts-password-security">
      <Row className="gx-2 aps-row">
        <Col md={6}>
          <InputField
            label="Email"
            placeholder="example@email.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled
          />
        </Col>
        <Col md={6}>
          <InputField label="Password" type="password" value={MASK} disabled />
        </Col>
      </Row>

      {currentPhase === phase.entry && (
        <>
          <div className="aps-section-title">Update password</div>

          <Row className="gx-2 aps-row">
            <Col md={6}>
              <InputField
                label="Enter new password"
                placeholder="Type your new password here"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                rightIcon={
                  <i
                    className={`ti ${showNew ? 'ti-eye-off' : 'ti-eye'}`}
                    title={showNew ? 'Hide password' : 'Show password'}
                    aria-label={showNew ? 'Hide password' : 'Show password'}
                  />
                }
                onRightIconClick={() => setShowNew((v) => !v)}
              />
            </Col>
            <Col md={6}>
              <InputField
                label="Confirm new password"
                placeholder="Re-type your new password here"
                type={showConfirm ? 'text' : 'password'}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                rightIcon={
                  <i
                    className={`ti ${showConfirm ? 'ti-eye-off' : 'ti-eye'}`}
                    title={showConfirm ? 'Hide password' : 'Show password'}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  />
                }
                onRightIconClick={() => setShowConfirm((v) => !v)}
              />
            </Col>
          </Row>
          <div className="aps-pw-rules-title">Password criteria</div>
          <div className="aps-pw-rules" role="list">
            {pwRuleOrder.map((r: PwRule) => {
              const ok = rulePass(
                newPassword,
                {
                  username: profile?.username ?? '',
                  email: profile?.email ?? '',
                },
                r,
              )
              return (
                <div
                  key={r}
                  className={`aps-pw-rule ${ok ? 'is-pass' : ''}`}
                  role="listitem"
                >
                  <i
                    className={`ti ${ok ? 'ti-check' : 'ti-circle'}`}
                    aria-hidden="true"
                  />
                  <span>{pwRuleLabel[r]}</span>
                </div>
              )
            })}
            {confirmNewPassword.length > 0 && (
              <div
                className={`aps-pw-rule ${
                  newPassword === confirmNewPassword ? 'is-pass' : ''
                }`}
                role="listitem"
              >
                <i
                  className={`ti ${
                    newPassword === confirmNewPassword
                      ? 'ti-check'
                      : 'ti-circle'
                  }`}
                  aria-hidden="true"
                />
                <span>{passwordMatch.matchOk}</span>
              </div>
            )}
          </div>
        </>
      )}

      <PasswordOtpModal
        isOpen={currentPhase === phase.otp}
        email={profile?.email ?? ''}
        submitting={otpVerifying}
        resending={otpSending}
        errorText={otpError}
        onRequestClose={handleCloseOtp}
        onVerify={(code) => void handleVerify(code)}
        onResend={() => void handleResend()}
      />
    </div>
  )
}

export default PasswordSecurity
